"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import InputField from "@/app/components/input";
import User from "@/app/components/svg/user";
import PhoneDef from "@/app/components/svg/phone-def";
import Upload from "@/app/components/svg/upload";
import EmailLight from "@/app/components/svg/email-light";
import Button from "../button";
import BtnArrow from "../svg/btnarrow";
import Checkbox from "../checkbox";
import ThankYouModal from "../thank-you";
import Loader from "../svg/loader";

interface VacantiesFormProps {
    title: string;
    btntext: string;
    lang: string;
}

const VacantiesForm: React.FC<VacantiesFormProps> = ({ title, btntext, lang }) => {
    const router = useRouter();
    const [formData, setFormData] = useState({
        fullName: "",
        phoneNumber: "",
        email: "",
        cv: null as File | null,
        comments: "",
    });

    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isChecked, setIsChecked] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isSubmitted) {
            setTimeout(() => setIsVisible(true), 50);
            setTimeout(() => {
                setIsVisible(false);
                setTimeout(() => router.push(`${lang === 'en' ? '/en' : ''}/who-we-are`), 500);
            }, 5000);
        }
    }, [isSubmitted, router]);

    const handleChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFormData(prev => ({ ...prev, cv: e.target.files![0] }));
        }
    };

    const handleCheckboxChange = (checked: boolean) => {
        setIsChecked(checked);
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        const requiredText = lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt'

        if (!formData.fullName) newErrors.fullName = requiredText;
        if (!formData.phoneNumber) newErrors.phoneNumber = requiredText;
        if (!formData.email) newErrors.email = requiredText;
        if (!formData.cv) newErrors.cv = lang === 'en' ? 'CV file is required' : 'CV-fil krävs'
        if (!isChecked) newErrors.checkbox = lang === 'en' ? 'Agreement to terms is required' : 'Godkännande av villkor krävs'

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);
        setSubmitError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('fullName', formData.fullName);
            formDataToSend.append('phoneNumber', formData.phoneNumber);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('comments', formData.comments);
            
            if (formData.cv) {
                formDataToSend.append('cv', formData.cv);
            }

            const response = await fetch('/api/send-vacancy', {
                method: 'POST',
                body: formDataToSend,
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.error || 'Failed to submit application');
            }

            setIsSubmitted(true);
            setFormData({
                fullName: "",
                phoneNumber: "",
                email: "",
                cv: null,
                comments: "",
            });
            setIsChecked(false);
        } catch (error) {
            console.error('Submission error:', error);
            setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative">
            {isSubmitted && (
                <div className={`fixed inset-0 flex items-center justify-center bg-[#00000080] z-10 transition-opacity duration-500 ease-in-out 
                    ${isVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                    <div className={`transform transition-transform duration-500 ease-in-out 
                        ${isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
                        <ThankYouModal title="Thank you!" text="Your application has been submitted." />
                    </div>
                </div>
            )}

            <div className="rounded-[40px] p-[48px] shadow-[0_10px_24px_0_#00000026] max-[576px]:p-[20px] max-[576px]:rounded-[20px]">
                <h2 className="text-[32px] font-bold text-violet-500 mb-[40px] max-[576px]:text-[20px]">{title}</h2>
                <form onSubmit={handleSubmit} className="space-y-[32px] max-[576px]:space-y-[24px]">
                    {submitError && (
                        <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">
                            {submitError}
                        </div>
                    )}
                    
                    <InputField
                        label={lang === 'en' ? 'Full Name' : 'fullständigt namn'}
                        iconLeft={<User />}
                        placeholder={lang === 'en' ? 'Write your full name' : 'Skriv ditt fullständigt namn'}
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => handleChange("fullName", e.target.value)}
                        errorMessage={errors.fullName}
                    />
                    
                    <InputField
                        label={lang === 'en' ? 'Phone Number' : 'telefonnummer'}
                        iconLeft={<PhoneDef />}
                        placeholder={lang === 'en' ? 'Write your phone number' : 'Skriv ditt telefonnummer'}
                        type="number"
                        value={formData.phoneNumber}
                        onChange={(e) => handleChange("phoneNumber", e.target.value)}
                        errorMessage={errors.phoneNumber}
                    />
                    
                    <InputField
                        label={lang === 'en' ? 'E-mail' : 'e-post'}
                        iconLeft={<EmailLight />}
                        placeholder={lang === 'en' ? 'Write your e-mail' : 'Skriv din e-post'}
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        errorMessage={errors.email}
                    />
                    
                    <div className="relative">
                        <input
                            type="file"
                            id="cv-upload"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className={`flex items-center border-[1.5px] rounded-[12px] pl-[14px] pr-[14px] py-[16px] max-[576px]:py-[12px] transition-all border-violet-50 ${errors.cv ? 'border-red-500' : 'border-gray-300'}`}>
                            <label
                                className="absolute -top-2 left-[14px] text-[12px] leading-[133%] tracking-[0.03em] uppercase bg-white px-[4px] py-[2px] transition-all max-[576px]:text-[10px] text-violet-300">
                                {lang === 'en' ? 'upload cv' : 'ladda upp cv'}
                            </label>
                            <div className="mr-3">
                                <Upload />
                            </div>
                            <div className="flex-1">
                                <label htmlFor="cv-upload" className="block text-sm font-medium text-gray-700">
                                    {formData.cv?.name ||  lang === 'en' ? 'Upload your CV' : 'Ladda upp ditt CV'}
                                </label>
                                {formData.cv && (
                                    <p className="text-xs text-gray-500 mt-1">{formData.cv.name}</p>
                                )}
                            </div>
                        </div>
                        {errors.cv && <p className="mt-1 text-sm text-red-500">{errors.cv}</p>}
                    </div>
                    
                    <InputField
                        label={lang === 'en' ? 'Additional Comments (Optional)' : 'ytterligare kommentarer (valfritt)'}
                        type="textarea"
                        placeholder={lang === 'en' ? 'Write text' : 'Skriv text'}
                        value={formData.comments}
                        onChange={(e) => handleChange("comments", e.target.value)}
                    />
                    
                    <Checkbox
                        label={lang === 'en' ? 'I consent to my personal data being processed in accordance with the privacy policy' : 'Jag samtycker till att mina personuppgifter behandlas i enlighet med'}
                        required={true}
                        checked={isChecked}
                        onChange={handleCheckboxChange}
                        link={`${lang === 'en' ? '/en' : ''}/privacy-policy`}
                        linktext={lang === 'en' ? 'privacy policy' : 'integritetspolicyn'}
                    />
                    {errors.checkbox && <span className="text-red-500 text-sm">{errors.checkbox}</span>}
                    
                    <div className="flex justify-end">
                        <Button
                            size="big"
                            type="primary"
                            text={isLoading ? lang === 'en' ? 'Sending...' : 'Sändning...' : btntext}
                            icon={isLoading ? <Loader /> : <BtnArrow />}
                            iconPostiton="right"
                            mobileFull="yes"
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VacantiesForm;